import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Input, Select, Sheet, Empty, Chip, Skeleton } from "../components/ui";
import { Users, PlusCircle, UserRound, AlertCircle, Lock, ArrowRight, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ShareJoinStepsButton from "../components/ShareJoinStepsButton";

const ROLES = ["batter", "bowler", "all-rounder", "wicket-keeper"];

export default function MembersPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { club, refreshMe } = useAuth();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [memberLimit, setMemberLimit] = useState(null);
  const canManage = ["owner", "admin", "manager"].includes(club?.my_role);

  const [form, setForm] = useState({
    name: "", mobile: "", role: "batter", batting_style: "", bowling_style: "", whatsapp: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitDetail, setLimitDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/members");
      setMembers(data);
      // Also fetch member limit from club settings
      if (club) {
        try {
          const { data: clubData } = await api.get(`/clubs/${club.id}/settings`);
          setMemberLimit(clubData.max_members || 30);
        } catch {}
      }
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("new") === "1") setOpen(true);
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/members", form);
      toast.success("Member added");
      setOpen(false);
      setForm({ name: "", mobile: "", role: "batter", batting_style: "", bowling_style: "", whatsapp: "", notes: "" });
      await load();
      await refreshMe();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        setLimitDetail({
          message: detail.message || "Member limit reached",
          currentPlan: detail.current_plan || "your current plan",
          maxMembers: detail.max_members || 0,
          currentMembers: detail.current_members || 0,
        });
        setShowLimitModal(true);
      } else {
        toast.error(apiError(err));
      }
    } finally { setSaving(false); }
  };

  // Guests (added to polls by admins) are listed separately and don't count towards the limit
  const guests = members ? members.filter((m) => m.guest) : [];
  const players = members ? members.filter((m) => !m.guest) : null;

  // Check if member limit is reached
  const isLimitReached = memberLimit && players && players.length >= memberLimit;

  return (
    <div className="pb-28">
      <TopBar
        title="Members"
        subtitle={`${players?.length || 0} players${guests.length ? ` • ${guests.length} guests` : ""}`}
      />

      <div className="px-5">
        {canManage && (
          <Card className="mb-4">
            <div className="font-semibold">Invite players with your club code</div>
            <p className="text-sm text-ink-muted mt-0.5 mb-3">
              Send your group the steps to join — they enter code <span className="font-mono font-bold text-ink">{club?.join_code}</span> and they're in.
            </p>
            <ShareJoinStepsButton club={club} />
          </Card>
        )}

        {/* Member Limit Display */}
        {memberLimit && (
          <div className="mb-4 p-3 rounded-xl bg-brand-50 border border-brand-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-brand-700">
                <Users size={16} />
                <span className="font-medium">Members</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`font-mono font-bold ${players && players.length >= (memberLimit || 30) ? "text-red-500" : "text-brand"}`}>
                  {players?.length || 0}
                </span>
                <span className="text-ink-muted">/</span>
                <span className="font-mono font-bold">{memberLimit}</span>
              </div>
            </div>
            <div className="mt-2 h-2 bg-brand-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${players && players.length >= (memberLimit || 30) ? "bg-red-500" : "bg-brand"}`}
                style={{ width: `${Math.min(100, ((players?.length || 0) / (memberLimit || 30)) * 100)}%` }}
              />
            </div>
            {isLimitReached && (
              <p className="text-xs text-amber-600 mt-1 text-center">Member limit reached. Upgrade plan to add more members.</p>
            )}
          </div>
        )}

        {players === null ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : players.length === 0 && guests.length === 0 ? (
          <Card className="p-0">
            <Empty icon={Users} title="No members yet" subtitle={canManage ? "Share the joining steps above with your players to get started." : "Invite players from the Invitations page to get started."} />
          </Card>
        ) : (
          <div className="space-y-2.5">
            {[...players, ...guests].map((m, i) => (
              <React.Fragment key={m.id}>
              {i === players.length && (
                <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold pt-3 pb-0.5">
                  Guests ({guests.length})
                </div>
              )}
              <Card
                key={m.id}
                className="py-3 px-4 flex items-center gap-3 cursor-pointer"
                onClick={() => nav(`/members/${m.id}`)}
                data-testid={`member-card-${m.id}`}
              >
                <Avatar member={m} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{m.name}</div>
                  <div className="text-xs text-ink-muted capitalize">{m.guest ? "Guest player" : m.role}</div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${m.balance < 0 ? "text-red-500" : "text-brand"}`}>
                    {inr(m.balance)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold">
                    balance
                  </div>
                </div>
              </Card>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <Sheet open={open} onClose={() => setOpen(false)} title="Add Member">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Name *" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="new-member-name" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Mobile" value={form.mobile} onChange={(e) => setForm({...form, mobile: e.target.value})} data-testid="new-member-mobile" />
            <Select label="Role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="new-member-role">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Batting" placeholder="Right-hand" value={form.batting_style} onChange={(e) => setForm({...form, batting_style: e.target.value})} />
            <Input label="Bowling" placeholder="Off-spin" value={form.bowling_style} onChange={(e) => setForm({...form, bowling_style: e.target.value})} />
          </div>
          <Input label="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({...form, whatsapp: e.target.value})} />
          <Button type="submit" size="lg" className="w-full" disabled={saving || isLimitReached} data-testid="new-member-submit">
            {saving ? "Saving…" : isLimitReached ? "Limit Reached" : "Add member"}
          </Button>
        </form>
      </Sheet>

      {/* Member Limit Modal */}
      {showLimitModal && limitDetail && (
        <div className="fixed inset-0 z-50" data-testid="member-limit-modal-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowLimitModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-5">
          <div className="bg-white rounded-3xl p-6 pb-8 w-full max-w-[400px] shadow-xl animate-in zoom-in-95 fade-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-ink">Member Limit Reached</h3>
              <button onClick={() => setShowLimitModal(false)} className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10 grid place-items-center" aria-label="Close">
                <X size={20} className="text-ink-muted" />
              </button>
            </div>
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mx-auto mb-4">
                <Users size={32} className="text-brand" />
              </div>
              <p className="text-sm text-ink-muted mb-2">{limitDetail.message}</p>
              <p className="text-sm text-ink-muted">
                Current plan: <span className="font-semibold text-ink">{limitDetail.currentPlan}</span>
              </p>
              <p className="text-sm text-ink-muted mt-1">
                Members: <span className="font-mono font-bold text-ink">{limitDetail.currentMembers}/{limitDetail.maxMembers}</span>
              </p>
            </div>
            <div className="space-y-3">
              <Button variant="primary" size="lg" className="w-full" onClick={() => { setShowLimitModal(false); nav("/pricing", { replace: true }); }} data-testid="member-limit-view-plans">
                <ArrowRight size={20} /> View Plans
              </Button>
              <Button variant="outline" size="lg" className="w-full" onClick={() => setShowLimitModal(false)} data-testid="member-limit-cancel">
                OK
              </Button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Avatar({ member, size = 44 }) {
  const initials = (member?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  if (member?.photo) {
    return <img src={member.photo} alt="" className="rounded-2xl object-cover" style={{ height: size, width: size }} />;
  }
  return (
    <div
      className="rounded-2xl bg-brand-50 text-brand font-bold grid place-items-center"
      style={{ height: size, width: size, fontSize: size * 0.36 }}
    >
      {initials || <UserRound size={size * 0.5} />}
    </div>
  );
}
